/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Balance Verification Page
 * ============================================================================
 *
 * SuperAdmin page for verifying journal balances match entity balances.
 * Displays discrepancies with expandable investigation guidance per account type.
 * Features org selector, status banner, summary stats, and discrepancy table.
 *
 * ============================================================================
 */

import { useState, useMemo, useCallback } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Search,
  Scale,
  Building2,
  Banknote,
  Landmark,
  Wallet,
  Smartphone,
  HelpCircle,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useVerifyBalances } from '@/hooks/api/useJournalLedger';
import { useOrganizations } from '@/hooks/api/useOrganizations';
import type { DiscrepancyDto } from '@/services/superadmin/journal-ledger.service';

// ============================================================================
// Investigation tips by account type
// ============================================================================

function getInvestigationTips(
  accountType: string,
  t: (key: string) => string
): string[] {
  switch (accountType) {
    case 'CASH_REGISTER':
      return [
        t('platform:journal.verification.tips.cashRegister.unrecorded'),
        t('platform:journal.verification.tips.cashRegister.physical'),
        t('platform:journal.verification.tips.cashRegister.pending'),
      ];
    case 'BANK_ACCOUNT':
      return [
        t('platform:journal.verification.tips.bankAccount.imported'),
        t('platform:journal.verification.tips.bankAccount.fees'),
        t('platform:journal.verification.tips.bankAccount.transfers'),
      ];
    case 'PARTNER_WALLET':
      return [
        t('platform:journal.verification.tips.partnerWallet.multiCurrency'),
        t('platform:journal.verification.tips.partnerWallet.fxRate'),
        t('platform:journal.verification.tips.partnerWallet.deposits'),
      ];
    case 'MOBILE_WALLET':
      return [
        t('platform:journal.verification.tips.mobileWallet.callbacks'),
        t('platform:journal.verification.tips.mobileWallet.pendingTransfers'),
      ];
    default:
      return [
        t('platform:journal.verification.tips.generic.compareRecent'),
        t('platform:journal.verification.tips.generic.snapshotTiming'),
      ];
  }
}

function getAccountTypeIcon(accountType: string) {
  switch (accountType) {
    case 'CASH_REGISTER':
      return Banknote;
    case 'BANK_ACCOUNT':
      return Landmark;
    case 'PARTNER_WALLET':
      return Wallet;
    case 'MOBILE_WALLET':
      return Smartphone;
    default:
      return HelpCircle;
  }
}

// ============================================================================
// Component
// ============================================================================

export function BalanceVerificationPage() {
  const { t } = useTranslation('platform');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  // Fetch orgs (first page, size=100)
  const {
    data: orgsData,
    isLoading: orgsLoading,
  } = useOrganizations({ page: 0, size: 100 });

  // Fetch discrepancies for the selected org
  const {
    data: discrepancies,
    isLoading: verifyLoading,
    isFetching: verifyFetching,
    refetch,
  } = useVerifyBalances(selectedOrgId);

  const isVerifying = verifyLoading || verifyFetching;
  const organizations = orgsData?.content ?? [];
  const hasOrg = !!selectedOrgId;
  const hasData = hasOrg && !!discrepancies;
  const hasDiscrepancies = hasData && discrepancies.length > 0;
  const isBalanced = hasData && discrepancies.length === 0;

  // Summary stats
  const summaryStats = useMemo(() => {
    if (!discrepancies || discrepancies.length === 0) {
      return { count: 0, totalAbsDifference: 0 };
    }
    const totalAbsDifference = discrepancies.reduce(
      (sum, d) => sum + Math.abs(d.difference),
      0
    );
    return { count: discrepancies.length, totalAbsDifference };
  }, [discrepancies]);

  const toggleRow = useCallback((index: number) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  }, []);

  const handleOrgChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedOrgId(e.target.value);
      setExpandedRows({});
    },
    []
  );

  const handleRefresh = useCallback(() => {
    if (selectedOrgId) {
      refetch();
    }
  }, [selectedOrgId, refetch]);

  const formatAmount = useCallback((amount: number) => {
    return amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  return (
    <div className="bv-page">
      {/* Header */}
      <div className="bv-header">
        <div className="bv-header__title-row">
          <div className="bv-header__title-group">
            <Scale className="bv-header__icon" />
            <div>
              <h1 className="bv-header__title">
                {t('platform:journal.verification.title')}
              </h1>
              <p className="bv-header__subtitle">
                {t('platform:journal.verification.subtitle')}
              </p>
            </div>
          </div>
          {hasOrg && (
            <button
              className={`bv-refresh-btn ${isVerifying ? 'bv-refresh-btn--spinning' : ''}`}
              onClick={handleRefresh}
              disabled={isVerifying}
            >
              {isVerifying ? (
                <Loader2 className="bv-refresh-btn__icon bv-refresh-btn__icon--spin" />
              ) : (
                <RefreshCw className="bv-refresh-btn__icon" />
              )}
              <span>
                {isVerifying
                  ? t('platform:journal.verification.verifying')
                  : t('platform:journal.verification.refresh')}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Organization Selector */}
      <div className="bv-org-selector">
        <label className="bv-org-selector__label" htmlFor="bv-org-select">
          <Building2 className="bv-org-selector__label-icon" />
          {t('platform:journal.verification.selectOrganization')}
        </label>
        <select
          id="bv-org-select"
          className="bv-org-selector__select"
          value={selectedOrgId}
          onChange={handleOrgChange}
          disabled={orgsLoading}
        >
          <option value="">
            {orgsLoading
              ? t('platform:journal.verification.loadingOrgs')
              : t('platform:journal.verification.selectOrgPlaceholder')}
          </option>
          {organizations.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name} ({org.code})
            </option>
          ))}
        </select>
      </div>

      {/* Loading state */}
      {hasOrg && isVerifying && !discrepancies && (
        <div className="bv-loading">
          <Loader2 className="bv-loading__spinner" />
          <p className="bv-loading__text">
            {t('platform:journal.verification.verifyingBalances')}
          </p>
        </div>
      )}

      {/* Status Banner - All Balanced */}
      {isBalanced && (
        <div className="bv-banner bv-banner--success">
          <CheckCircle2 className="bv-banner__icon" />
          <div className="bv-banner__content">
            <h2 className="bv-banner__title">
              {t('platform:journal.verification.allBalanced')}
            </h2>
            <p className="bv-banner__desc">
              {t('platform:journal.verification.allBalancedDesc')}
            </p>
          </div>
        </div>
      )}

      {/* Status Banner - Discrepancies Found */}
      {hasDiscrepancies && (
        <div className="bv-banner bv-banner--error">
          <AlertTriangle className="bv-banner__icon" />
          <div className="bv-banner__content">
            <h2 className="bv-banner__title">
              {t('platform:journal.verification.discrepanciesFound')}
            </h2>
            <p className="bv-banner__desc">
              {t('platform:journal.verification.discrepanciesFoundDesc', {
                count: summaryStats.count,
              })}
            </p>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {hasDiscrepancies && (
        <div className="bv-summary">
          <div className="bv-summary__card">
            <span className="bv-summary__card-label">
              {t('platform:journal.verification.totalDiscrepancies')}
            </span>
            <span className="bv-summary__card-value bv-summary__card-value--error">
              {summaryStats.count}
            </span>
          </div>
          <div className="bv-summary__card">
            <span className="bv-summary__card-label">
              {t('platform:journal.verification.totalAbsDifference')}
            </span>
            <span className="bv-summary__card-value bv-summary__card-value--warning">
              {formatAmount(summaryStats.totalAbsDifference)}
            </span>
          </div>
        </div>
      )}

      {/* Discrepancy Table */}
      {hasDiscrepancies && (
        <div className="bv-table-wrapper">
          <table className="bv-table">
            <thead>
              <tr>
                <th className="bv-table__th bv-table__th--expand" />
                <th className="bv-table__th">
                  {t('platform:journal.verification.columns.accountCode')}
                </th>
                <th className="bv-table__th">
                  {t('platform:journal.verification.columns.accountType')}
                </th>
                <th className="bv-table__th">
                  {t('platform:journal.verification.columns.referenceId')}
                </th>
                <th className="bv-table__th bv-table__th--right">
                  {t('platform:journal.verification.columns.journalBalance')}
                </th>
                <th className="bv-table__th bv-table__th--right">
                  {t('platform:journal.verification.columns.entityBalance')}
                </th>
                <th className="bv-table__th bv-table__th--right">
                  {t('platform:journal.verification.columns.difference')}
                </th>
              </tr>
            </thead>
            <tbody>
              {discrepancies.map((d: DiscrepancyDto, idx: number) => {
                const isExpanded = !!expandedRows[idx];
                const TypeIcon = getAccountTypeIcon(d.accountType);
                const tips = getInvestigationTips(d.accountType, t);
                const diffClass =
                  d.difference > 0
                    ? 'bv-diff--positive'
                    : d.difference < 0
                      ? 'bv-diff--negative'
                      : '';

                return (
                  <DiscrepancyRow
                    key={`${d.accountId}-${d.referenceId}-${idx}`}
                    discrepancy={d}
                    index={idx}
                    isExpanded={isExpanded}
                    onToggle={toggleRow}
                    TypeIcon={TypeIcon}
                    tips={tips}
                    diffClass={diffClass}
                    formatAmount={formatAmount}
                    t={t}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state: org selected but no data yet and not loading */}
      {hasOrg && !isVerifying && !discrepancies && (
        <div className="bv-empty">
          <Search className="bv-empty__icon" />
          <p className="bv-empty__text">
            {t('platform:journal.verification.clickRefresh')}
          </p>
        </div>
      )}

      {/* No org selected */}
      {!hasOrg && (
        <div className="bv-empty">
          <Building2 className="bv-empty__icon" />
          <p className="bv-empty__text">
            {t('platform:journal.verification.selectOrgPrompt')}
          </p>
        </div>
      )}

      <style>{pageStyles}</style>
    </div>
  );
}

// ============================================================================
// Discrepancy Row Sub-component
// ============================================================================

function DiscrepancyRow({
  discrepancy,
  index,
  isExpanded,
  onToggle,
  TypeIcon,
  tips,
  diffClass,
  formatAmount,
  t,
}: {
  discrepancy: DiscrepancyDto;
  index: number;
  isExpanded: boolean;
  onToggle: (index: number) => void;
  TypeIcon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  tips: string[];
  diffClass: string;
  formatAmount: (n: number) => string;
  t: (key: string) => string;
}) {
  return (
    <>
      <tr
        className={`bv-table__row ${isExpanded ? 'bv-table__row--expanded' : ''}`}
        style={{ animationDelay: `${index * 0.04}s` }}
      >
        <td className="bv-table__td bv-table__td--expand">
          <button
            className="bv-expand-btn"
            onClick={() => onToggle(index)}
            aria-label={
              isExpanded
                ? t('platform:journal.verification.collapseRow')
                : t('platform:journal.verification.expandRow')
            }
          >
            {isExpanded ? (
              <ChevronDown className="bv-expand-btn__icon" />
            ) : (
              <ChevronRight className="bv-expand-btn__icon" />
            )}
          </button>
        </td>
        <td className="bv-table__td bv-table__td--code">
          {discrepancy.accountCode}
        </td>
        <td className="bv-table__td">
          <span className="bv-account-type">
            <TypeIcon className="bv-account-type__icon" />
            <span>{discrepancy.accountType.replace(/_/g, ' ')}</span>
          </span>
        </td>
        <td className="bv-table__td bv-table__td--ref">
          {discrepancy.referenceId}
        </td>
        <td className="bv-table__td bv-table__td--right bv-table__td--mono">
          {formatAmount(discrepancy.journalBalance)}
        </td>
        <td className="bv-table__td bv-table__td--right bv-table__td--mono">
          {formatAmount(discrepancy.entityBalance)}
        </td>
        <td className={`bv-table__td bv-table__td--right bv-table__td--mono ${diffClass}`}>
          {discrepancy.difference > 0 ? '+' : ''}
          {formatAmount(discrepancy.difference)}
        </td>
      </tr>
      {isExpanded && (
        <tr className="bv-table__tips-row">
          <td colSpan={7} className="bv-table__tips-cell">
            <div className="bv-tips">
              <div className="bv-tips__header">
                <Search className="bv-tips__header-icon" />
                <span className="bv-tips__header-title">
                  {t('platform:journal.verification.investigationTips')}
                </span>
              </div>
              <ul className="bv-tips__list">
                {tips.map((tip, i) => (
                  <li key={i} className="bv-tips__item">
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ============================================================================
// Styles
// ============================================================================

const pageStyles = `
/* Page layout */
.bv-page {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem 2rem;
  width: 100%;
  box-sizing: border-box;
  min-height: 100%;
}

/* Header */
.bv-header {
  animation: bv-header-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.bv-header__title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.bv-header__title-group {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.bv-header__icon {
  width: 28px;
  height: 28px;
  color: var(--color-brand-primary);
  margin-top: 2px;
  flex-shrink: 0;
}
.bv-header__title {
  font-family: var(--font-heading);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.3;
}
.bv-header__subtitle {
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  margin: 0.15rem 0 0;
}

/* Refresh button */
.bv-refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.1rem;
  border-radius: var(--radius-md);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--color-border-default);
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
  transition: all 0.2s ease;
}
.bv-refresh-btn:hover:not(:disabled) {
  border-color: var(--color-brand-primary);
  color: var(--color-brand-primary);
}
.bv-refresh-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.bv-refresh-btn__icon {
  width: 15px;
  height: 15px;
}
.bv-refresh-btn__icon--spin {
  animation: bv-spin 1s linear infinite;
}

/* Organization Selector */
.bv-org-selector {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  padding: 1rem 1.25rem;
  box-shadow: var(--shadow-sm);
  animation: bv-card-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.06s both;
}
.bv-org-selector__label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-text-secondary);
}
.bv-org-selector__label-icon {
  width: 15px;
  height: 15px;
}
.bv-org-selector__select {
  width: 100%;
  max-width: 480px;
  padding: 0.55rem 0.85rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--input-border);
  background: var(--input-bg);
  color: var(--color-text-primary);
  font-size: 0.88rem;
  font-family: var(--font-body);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  appearance: auto;
}
.bv-org-selector__select:focus {
  border-color: var(--color-brand-primary);
  box-shadow: 0 0 0 2px var(--input-focus-ring);
}
.bv-org-selector__select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading */
.bv-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  animation: bv-card-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
}
.bv-loading__spinner {
  width: 32px;
  height: 32px;
  color: var(--color-brand-primary);
  animation: bv-spin 1s linear infinite;
  margin-bottom: 1rem;
}
.bv-loading__text {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin: 0;
}

/* Status Banners */
.bv-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-radius: var(--radius-lg);
  animation: bv-card-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
}
.bv-banner--success {
  background: color-mix(in srgb, var(--color-success) 8%, var(--color-bg-secondary));
  border: 1px solid color-mix(in srgb, var(--color-success) 30%, transparent);
}
.bv-banner--success .bv-banner__icon {
  width: 36px;
  height: 36px;
  color: var(--color-success);
  flex-shrink: 0;
}
.bv-banner--error {
  background: color-mix(in srgb, var(--color-error) 8%, var(--color-bg-secondary));
  border: 1px solid color-mix(in srgb, var(--color-error) 30%, transparent);
}
.bv-banner--error .bv-banner__icon {
  width: 36px;
  height: 36px;
  color: var(--color-error);
  flex-shrink: 0;
}
.bv-banner__content {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.bv-banner__title {
  font-family: var(--font-heading);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
}
.bv-banner__desc {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  margin: 0;
}

/* Summary Stats */
.bv-summary {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  animation: bv-card-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.18s both;
}
.bv-summary__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  padding: 1.1rem 1rem;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  text-align: center;
}
.bv-summary__card-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.bv-summary__card-value {
  font-family: var(--font-heading);
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.1;
}
.bv-summary__card-value--error {
  color: var(--color-error);
}
.bv-summary__card-value--warning {
  color: var(--color-warning);
}

/* Table wrapper */
.bv-table-wrapper {
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow-x: auto;
  animation: bv-card-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.24s both;
}

/* Table */
.bv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.bv-table__th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 650;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-muted);
  border-bottom: 1px solid var(--color-border-subtle);
  white-space: nowrap;
  background: var(--color-bg-tertiary);
}
.bv-table__th--right {
  text-align: right;
}
.bv-table__th--expand {
  width: 40px;
  padding: 0.75rem 0.5rem 0.75rem 1rem;
}

.bv-table__row {
  animation: bv-row-in 0.3s cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: background 0.15s ease;
}
.bv-table__row:hover {
  background: var(--color-bg-tertiary);
}
.bv-table__row--expanded {
  background: var(--color-bg-tertiary);
}

.bv-table__td {
  padding: 0.7rem 1rem;
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-border-subtle);
  vertical-align: middle;
}
.bv-table__td--expand {
  width: 40px;
  padding: 0.7rem 0.5rem 0.7rem 1rem;
}
.bv-table__td--code {
  font-weight: 600;
  font-family: monospace;
  font-size: 0.82rem;
}
.bv-table__td--ref {
  font-family: monospace;
  font-size: 0.78rem;
  color: var(--color-text-secondary);
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bv-table__td--right {
  text-align: right;
}
.bv-table__td--mono {
  font-family: monospace;
  font-size: 0.82rem;
  font-weight: 600;
}

/* Difference colors */
.bv-diff--positive {
  color: var(--color-error) !important;
}
.bv-diff--negative {
  color: var(--color-info) !important;
}

/* Expand button */
.bv-expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all 0.15s ease;
}
.bv-expand-btn:hover {
  color: var(--color-brand-primary);
  background: color-mix(in srgb, var(--color-brand-primary) 10%, transparent);
}
.bv-expand-btn__icon {
  width: 16px;
  height: 16px;
}

/* Account type badge */
.bv-account-type {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  text-transform: capitalize;
}
.bv-account-type__icon {
  width: 14px;
  height: 14px;
  color: var(--color-text-muted);
}

/* Investigation tips row */
.bv-table__tips-row {
  animation: bv-tips-in 0.25s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.bv-table__tips-cell {
  padding: 0 1rem 0.75rem 1rem;
  border-bottom: 1px solid var(--color-border-subtle);
  background: var(--color-bg-tertiary);
}

.bv-tips {
  padding: 0.85rem 1rem;
  background: color-mix(in srgb, var(--color-warning) 6%, var(--color-bg-secondary));
  border: 1px solid color-mix(in srgb, var(--color-warning) 20%, transparent);
  border-radius: var(--radius-md);
  margin-top: 0.25rem;
}
.bv-tips__header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
}
.bv-tips__header-icon {
  width: 14px;
  height: 14px;
  color: var(--color-warning);
}
.bv-tips__header-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-text-primary);
}
.bv-tips__list {
  margin: 0;
  padding: 0 0 0 1.25rem;
  list-style: disc;
}
.bv-tips__item {
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin-bottom: 0.15rem;
}
.bv-tips__item:last-child {
  margin-bottom: 0;
}

/* Empty state */
.bv-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  text-align: center;
  animation: bv-card-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
}
.bv-empty__icon {
  width: 40px;
  height: 40px;
  color: var(--color-text-muted);
  margin-bottom: 1rem;
  opacity: 0.5;
}
.bv-empty__text {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin: 0;
  max-width: 360px;
}

/* Animations */
@keyframes bv-header-in {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes bv-card-in {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes bv-row-in {
  from {
    opacity: 0;
    transform: translateX(-6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
@keyframes bv-tips-in {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 300px;
  }
}
@keyframes bv-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 640px) {
  .bv-page {
    padding: 1rem;
  }
  .bv-header__title-row {
    flex-direction: column;
  }
  .bv-summary {
    grid-template-columns: 1fr;
  }
  .bv-banner {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }
  .bv-org-selector__select {
    max-width: 100%;
  }
  .bv-table {
    font-size: 0.78rem;
  }
  .bv-table__th,
  .bv-table__td {
    padding: 0.5rem 0.6rem;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .bv-header,
  .bv-org-selector,
  .bv-loading,
  .bv-banner,
  .bv-summary,
  .bv-table-wrapper,
  .bv-empty,
  .bv-table__row,
  .bv-table__tips-row {
    animation: none !important;
  }
  .bv-refresh-btn__icon--spin,
  .bv-loading__spinner {
    animation: none !important;
  }
}
`;

export default BalanceVerificationPage;
